/**
 * Preview mode: seeing unpublished content as it will look once published.
 *
 * Staff append `?mode=preview` to a YourTour URL. The client cannot decide
 * whether that is allowed — the server authorises it against the logged-in
 * user's permission and their access to that specific community, and answers
 * with a `previewMode` boolean on every in-scope endpoint. So there are two
 * separate facts here, and the UI needs both:
 *
 * - requested: the URL asked for preview.
 * - granted:   the server honoured it.
 *
 * When those disagree the page is showing published content to someone who
 * expected drafts, which is exactly the situation that generates a support
 * ticket. PreviewModeBanner says so rather than letting it pass silently.
 */

import { isPublicPlatform } from './publicPlatforms';

export const PREVIEW_MODE = 'preview';

/**
 * Preview mode is withdrawn from the 1.16 release and deferred to 1.17.
 *
 * The feature is threaded through every request the app makes, so it is
 * withdrawn by closing this one gate rather than by unpicking the call sites.
 * With it set, isPreviewRequested() is always false: no `mode=preview` is sent
 * to the API, PreviewModeBanner renders nothing, and the draft badge on map
 * pins stops appearing on its own because it is gated on the server's grant.
 * Mirrors $previewModeWithdrawn in CommunityResourceHelperTrait.php.
 *
 * Set to false to restore preview mode in 1.17. The rules below are unchanged
 * and still under test.
 */
export const PREVIEW_MODE_WITHDRAWN = true;

/**
 * The URL rules alone, independent of the release gate.
 *
 * Kept whole and under test while preview mode is withdrawn for 1.16, so
 * restoring it in 1.17 is a single flag change rather than a rewrite.
 *
 * @param {object} settings - App settings from the app store.
 * @returns {boolean} True when `?mode=preview` is set on a non-public platform.
 */
export function isPreviewRequestedByUrl( settings ) {
  const { mode, platform } = settings || {};

  if ( mode !== PREVIEW_MODE ) {
    return false;
  }

  return !isPublicPlatform( platform );
}

/**
 * Whether this page should ask the server for preview mode.
 *
 * Always false while preview mode is withdrawn for 1.16; see
 * PREVIEW_MODE_WITHDRAWN above.
 *
 * @param {object} settings - App settings from the app store (getUrlData output).
 * @returns {boolean} True when preview should be requested from the API.
 */
export function isPreviewRequested( settings ) {
  if ( PREVIEW_MODE_WITHDRAWN ) {
    return false;
  }

  return isPreviewRequestedByUrl( settings );
}

/**
 * The mode value to send to the API, or null when preview is not being asked for.
 *
 * Note `?mode` is a single-value parameter already used by kiosk and print,
 * so preview cannot combine with either.
 *
 * @param {object} settings - App settings from the app store.
 * @returns {string|null} 'preview', or null.
 */
export function getApiMode( settings ) {
  return isPreviewRequested( settings ) ? PREVIEW_MODE : null;
}
